// src/utils/assetProcessing.js
// ─── Currency Formatter ───────────────────────────────────────────────────────

export const formatCurrencyValue = (value) => {
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000)   return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000)     return `₹${(value / 1000).toFixed(1)}K`;
  return `₹${value.toLocaleString()}`;
};

// ─── Time Utilities ───────────────────────────────────────────────────────────

export const getTimeAgo = (date) => {
  if (!date) return 'Unknown';
  const diff = Math.floor((new Date() - date) / 1000);
  if (diff < 60)    return 'Just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  return `${Math.floor(diff / 86400)} days ago`;
};

/**
 * Safely converts a Firestore Timestamp or ISO string to a JS Date.
 */
export const toDate = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  return new Date(value);
};

export const EMPTY_METRICS = {
  totalAssets:      0,
  activeAssets:     0,
  assignedAssets:   0,
  unassignedAssets: 0,
  inRepairAssets:   0,
  disposedAssets:   0,
  totalValue:       0,
  warrantyExpiring: 0,
  alertsCount:      0,
  usersCount:       0,
  locationsCount:   0,
};

// ─── Category Color Map ───────────────────────────────────────────────────────

const CATEGORY_COLORS = {
  'Information Technology': '#4a90e2',
  Software:                 '#e94b8a',
  'General Assets':         '#a4c65b',
  Furniture:                '#ff7d3b',
  Vehicles:                 '#f5c842',
  Hardware:                 '#4a90e2',
  Uncategorized:            '#6B7280',
};

// ─── Simple Field Resolvers (direct field access) ──────────────────────────

const resolveCategory = (a, categoriesMap) => {
  if (!a) return 'Uncategorized';

  // 1. Try ID lookup
  const id = a.categoryId || a.category_id || null;
  if (id && categoriesMap) {
    const cat = categoriesMap.get(id);
    if (cat) {
      return cat.categoryName || cat.name || 'Uncategorized';
    }
  }

  // 2. Fall back to inline name fields
  const val = a.categoryName || a.category || a.category_name || '';
  return val || 'Uncategorized';
};
const resolveSubCategory = (a, subCategoriesMap) => {
  if (!a) return 'Unspecified Subcategory';

  const id = a.subCategoryId || a.subcategoryId || a.sub_category_id || null;
  if (id && subCategoriesMap) {
    const sc = subCategoriesMap.get(id);
    if (sc) {
      return sc.subCategoryName || sc.subcategoryName || sc.name || 'Unspecified Subcategory';
    }
  }

  const val = a.subCategoryName || a.subCategory || a.subcategoryName || a.subcategory || '';
  return val || 'Unspecified Subcategory';
};

const resolveBrand = (a, brandsMap) => {
  if (!a) return 'Unknown Brand';

  const id = a.brandId || a.brand_id || null;
  if (id && brandsMap) {
    const b = brandsMap.get(id);
    if (b) {
      return b.brandName || b.name || 'Unknown Brand';
    }
  }

  const val = a.brandName || a.brand || a.brand_name || a.manufacturer || '';
  return val || 'Unknown Brand';
};

const resolveLocation = (a, locationsMap) => {
  if (!a) return 'Unknown Location';

  const code = a.locationCode || a.location_code || null;
  if (code && locationsMap) {
    const loc = locationsMap.get(code);
    if (loc) {
      return loc.locationName || loc.name || loc.locationCode || 'Unknown Location';
    }
  }

  return a.currentLocationName || a.locationName || 'Unknown Location';
};

// ─── Main Asset Processor ─────────────────────────────────────────────────────

export const processAssetsData = (assets, {
  categoriesMap    = new Map(),
  subCategoriesMap = new Map(),
  brandsMap        = new Map(),
  locationsMap     = new Map(),
} = {}) => {
  console.log('[processAssetsData] Processing', assets?.length || 0, 'assets');
  
  if (!Array.isArray(assets) || assets.length === 0) {
    console.log('[processAssetsData] No assets, returning empty');
    return {
      metrics: { ...EMPTY_METRICS },
      categoryData: [],
      subCategoryData: [],
      brandData: [],
      locationData: [],
      monthlyTrend: [],
      activities: [],
    };
  }

  // Log sample data
  const sample = assets[0];
  console.log('[processAssetsData] Sample category:', sample.categoryName);
  console.log('[processAssetsData] Sample subcategory:', sample.subCategoryName);
  console.log('[processAssetsData] Sample brand:', sample.brandName);

  const now = new Date();
  const thirtyDaysLater = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  // ── Core Metrics ─────────────────────────────────────────────────────────
  const totalAssets = assets.length;
  const activeAssets = assets.filter((a) =>
    ['available', 'active'].includes((a.status || '').toLowerCase()),
  ).length;
  const assignedAssets = assets.filter(
    (a) => a.assignedTo || (a.assignedToName && a.assignedToName !== 'Unassigned'),
  ).length;
  const inRepairAssets = assets.filter((a) =>
    ['in repair', 'maintenance'].includes((a.status || '').toLowerCase()),
  ).length;
  const disposedAssets = assets.filter(
    (a) => (a.status || '').toLowerCase() === 'disposed',
  ).length;
  const totalValue = assets.reduce(
    (sum, a) => sum + (parseFloat(a.purchaseValue) || 0), 0,
  );
  const warrantyExpiring = assets.filter((a) => {
    const wDate = toDate(a.warrantyExpiry);
    if (!wDate) return false;
    return wDate >= now && wDate <= thirtyDaysLater;
  }).length;

  // ── Category Groups ───────────────────────────────────────────────────────
    const categoryGroups = {};
  assets.forEach((a) => {
    const cat = resolveCategory(a, categoriesMap);
    if (!categoryGroups[cat]) categoryGroups[cat] = { count: 0, value: 0 };
    categoryGroups[cat].count++;
    categoryGroups[cat].value += parseFloat(a.purchaseValue) || 0;
  });

  const categoryData = Object.entries(categoryGroups)
    .map(([name, data], idx) => ({
      name: name.length > 20 ? `${name.substring(0, 20)}...` : name,
      fullName: name,
      value: data.count,
      fill: CATEGORY_COLORS[name] || `hsl(${idx * 60}, 70%, 50%)`,
      color: CATEGORY_COLORS[name] || `hsl(${idx * 60}, 70%, 50%)`,
      totalValue: data.value,
    }))
    .sort((a, b) => b.value - a.value);

  console.log('[processAssetsData] Categories found:', categoryData.length);

  // ── Subcategory Groups ────────────────────────────────────────────────────
   const subCategoryGroups = {};
  assets.forEach((a) => {
    const sub = resolveSubCategory(a, subCategoriesMap);
    // Skip empty/unspecified
    if (!sub || sub === 'Unspecified Subcategory' || sub === '' || sub === 'N/A') return;
    if (!subCategoryGroups[sub]) subCategoryGroups[sub] = { count: 0, value: 0 };
    subCategoryGroups[sub].count++;
    subCategoryGroups[sub].value += parseFloat(a.purchaseValue) || 0;
  });

  const subCategoryData = Object.entries(subCategoryGroups)
    .map(([name, data], idx) => ({
      name: name.length > 20 ? `${name.substring(0, 20)}...` : name,
      fullName: name,
      value: data.count,
      fill: `hsl(${(idx * 55 + 180) % 360}, 70%, 50%)`,
      color: `hsl(${(idx * 55 + 180) % 360}, 70%, 50%)`,
      totalValue: data.value,
    }))
    .sort((a, b) => b.value - a.value);

  console.log('[processAssetsData] Subcategories found:', subCategoryData.length);
  if (subCategoryData.length > 0) {
    console.log('[processAssetsData] Sample subcategories:', subCategoryData.slice(0, 3));
  }

  // ── Brand Groups ──────────────────────────────────────────────────────────
  const brandGroups = {};
  assets.forEach((a) => {
    const brand = resolveBrand(a, brandsMap);
    // Skip empty/unknown
    if (!brand || brand === 'Unknown Brand' || brand === '' || brand === 'N/A') return;
    if (!brandGroups[brand]) brandGroups[brand] = { count: 0, value: 0 };
    brandGroups[brand].count++;
    brandGroups[brand].value += parseFloat(a.purchaseValue) || 0;
  });

  const brandData = Object.entries(brandGroups)
    .map(([name, data], idx) => ({
      name: name.length > 20 ? `${name.substring(0, 20)}...` : name,
      fullName: name,
      value: data.count,
      fill: `hsl(${(idx * 75 + 120) % 360}, 65%, 45%)`,
      color: `hsl(${(idx * 75 + 120) % 360}, 65%, 45%)`,
      totalValue: data.value,
    }))
    .sort((a, b) => b.value - a.value);

  console.log('[processAssetsData] Brands found:', brandData.length);
  if (brandData.length > 0) {
    console.log('[processAssetsData] Sample brands:', brandData.slice(0, 3));
  }

  // ── Location Groups ───────────────────────────────────────────────────────
  const locationGroups = {};
  const uniqueLocations = new Set();
  assets.forEach((a) => {
    const loc = resolveLocation(a, locationsMap);
    if (!locationGroups[loc]) locationGroups[loc] = { assets: 0, value: 0 };
    locationGroups[loc].assets++;
    locationGroups[loc].value += parseFloat(a.purchaseValue) || 0;
    uniqueLocations.add(loc);
  });

  const locationData = Object.entries(locationGroups)
    .map(([loc, data]) => ({
      location: loc,
      fullLocation: loc,
      assets: data.assets,
      value: data.value,
    }))
    .sort((a, b) => b.assets - a.assets);

  // ── Monthly Trend ─────────────────────────────────────────────────────────
  const monthlyTrend = [];
  for (let i = 5; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const mName = date.toLocaleDateString('en-US', { month: 'short' });

    const added = assets.filter((a) => {
      const cDate = toDate(a.createdAt);
      if (!cDate) return false;
      return (
        cDate.getMonth() === date.getMonth() &&
        cDate.getFullYear() === date.getFullYear()
      );
    }).length;

    const disposed = assets.filter((a) => {
      if ((a.status || '').toLowerCase() !== 'disposed') return false;
      const uDate = toDate(a.updatedAt);
      if (!uDate) return false;
      return (
        uDate.getMonth() === date.getMonth() &&
        uDate.getFullYear() === date.getFullYear()
      );
    }).length;

    monthlyTrend.push({ month: mName, added, disposed });
  }

  // ── Recent Activities ─────────────────────────────────────────────────────
  const activities = assets
    .filter((a) => a.createdAt || a.updatedAt)
    .sort((a, b) => {
      const tA = toDate(a.updatedAt || a.createdAt)?.getTime() ?? 0;
      const tB = toDate(b.updatedAt || b.createdAt)?.getTime() ?? 0;
      return tB - tA;
    })
    .slice(0, 5)
    .map((a, i) => {
      const cD = toDate(a.createdAt);
      const uD = toDate(a.updatedAt);
      const isNew = cD && uD && Math.abs(cD.getTime() - uD.getTime()) < 60000;
      return {
        id: i + 1,
        action: isNew ? 'Asset Added' : 'Asset Updated',
        details: `${a.assetName || 'Unknown Asset'} - ${a.assetId || 'No ID'}`,
        user: a.createdByEmail || a.updatedBy || 'System',
        time: getTimeAgo(uD || cD),
      };
    });

  const result = {
    metrics: {
      totalAssets,
      activeAssets,
      assignedAssets,
      unassignedAssets: totalAssets - assignedAssets,
      inRepairAssets,
      disposedAssets,
      totalValue,
      warrantyExpiring,
      alertsCount: warrantyExpiring,
      locationsCount: uniqueLocations.size,
    },
    categoryData,
    subCategoryData,
    brandData,
    locationData,
    monthlyTrend,
    activities,
  };

  console.log('[processAssetsData] FINAL RESULT:', {
    categories: result.categoryData.length,
    subCategories: result.subCategoryData.length,
    brands: result.brandData.length,
    locations: result.locationData.length,
  });

  return result;
};